import React from 'react';
import {FormattedMessage} from 'react-intl';
import {graphql, useLazyLoadQuery, usePaginationFragment} from 'react-relay/hooks';
import classNames from 'classnames';
import Button from '../../../../../components/button/button';
import QuoteItem from './quote-item';

const quotesQuery = graphql`
    query quotesListQuery($postId: String!, $includeIsAuthorized: Boolean!, $first: Int!, $after: String) {
        ...quotesList_query @arguments(postId: $postId, first: $first, after: $after, includeIsAuthorized: $includeIsAuthorized)
    }
`;

const QuotesList = React.memo(({postId, currentUserId, isAuthorized, highlightId}) => {
    const queryData = useLazyLoadQuery(quotesQuery, {
        postId,
        includeIsAuthorized: isAuthorized,
        first: 8,
        after: null
    });
    
    const {data, loadNext, hasNext, isLoadingNext, refetch} = usePaginationFragment(
        graphql`
            fragment quotesList_query on Query
            @refetchable(queryName: "QuotesListRefetchQuery")
            @argumentDefinitions(
                postId: {type: "String!"}
                first: {type: "Int", defaultValue: 8}
                after: {type: "String"}
                includeIsAuthorized: {type: "Boolean", defaultValue: false}
            ) {
                vibePostById(id: $postId) {
                    id
                    quotes(first: $first, after: $after) @connection(key: "QuotesList_quotes") {
                        edges {
                            node {
                                id
                                message
                                media
                                created
                                removed
                                spam
                                likesCount
                                likedByCurrentUser @include(if: $includeIsAuthorized)
                                stemLikesCount
                                stemLikedByCurrentUser @include(if: $includeIsAuthorized)
                                viewsCount
                                viewedByCurrentUser @include(if: $includeIsAuthorized)
                                commentedByCurrentUser @include(if: $includeIsAuthorized)
                                quotedByCurrentUser @include(if: $includeIsAuthorized)
                                commentsCount
                                quotesCount
                                sharesCount
                                sharedByCurrentUser @include(if: $includeIsAuthorized)
                                poll {
                                    id
                                    options
                                    optionVotesCounts
                                    pollVotesCount
                                    votedByCurrentUser
                                    votedOptionIndex
                                }
                                sharedPost {
                                    id
                                    message
                                    media
                                    created
                                    removed
                                    spam
                                    sharesCount
                                    quotesCount
                                    likesCount
                                    likedByCurrentUser @include(if: $includeIsAuthorized)
                                    stemLikesCount
                                    stemLikedByCurrentUser @include(if: $includeIsAuthorized)
                                    viewedByCurrentUser @include(if: $includeIsAuthorized)
                                    quotedByCurrentUser @include(if: $includeIsAuthorized)
                                    commentsCount
                                    author {
                                        id
                                        vibe {
                                            username
                                        }
                                        followingUser @include(if: $includeIsAuthorized)
                                    }
                                }
                                quotedPost {
                                    id
                                    message
                                    media
                                    created
                                    removed
                                    spam
                                    sharesCount
                                    quotesCount
                                    likesCount
                                    likedByCurrentUser @include(if: $includeIsAuthorized)
                                    stemLikesCount
                                    stemLikedByCurrentUser @include(if: $includeIsAuthorized)
                                    viewedByCurrentUser @include(if: $includeIsAuthorized)
                                    quotedByCurrentUser @include(if: $includeIsAuthorized)
                                    commentsCount
                                    poll {
                                        id
                                        options
                                        optionVotesCounts
                                        pollVotesCount
                                        votedByCurrentUser
                                        votedOptionIndex
                                    }
                                    author {
                                        id
                                        vibe {
                                            username
                                        }
                                        followingUser @include(if: $includeIsAuthorized)
                                    }
                                }
                                author {
                                    id
                                    vibe {
                                        username
                                    }
                                    followingUser @include(if: $includeIsAuthorized)
                                }
                            }
                        }
                        pageInfo {
                            hasNextPage
                            endCursor
                        }
                    }
                }
            }
        `,
        queryData
    );
    
    const quotes = data.vibePostById?.quotes?.edges || [];
    const quoteRefs = React.useRef(new Map());

    const getQuoteRef = React.useCallback((quoteId) => {
        if (!quoteRefs.current.has(quoteId)) {
            quoteRefs.current.set(quoteId, React.createRef());
        }
        return quoteRefs.current.get(quoteId);
    }, []);

    return (
        <div>
            {quotes.length === 0 && (
                <div className='text-md color-gray-500 padding-left-0dot75rem'>
                    <FormattedMessage defaultMessage='No quotes yet' />
                </div>
            )}
            {quotes.length > 0 && (
                <div>
                    {quotes.map((edge, index) => {
                        const quote = edge.node;
                        return (
                            <div key={quote.id} className={classNames({'padding-top-1rem': index > 0})}>
                                <div className='position-relative'>
                                    <div
                                        className='position-absolute top-1dot25rem border-bottom-style-solid border-bottom-width-0dot125rem border-bottom-color-gray-500 pointer-events-none'
                                        style={{left: '-0.8rem', width: '0.6rem'}}
                                    />
                                    <QuoteItem
                                        quote={quote}
                                        currentUserId={currentUserId}
                                        isAuthorized={isAuthorized}
                                        getQuoteRef={getQuoteRef}
                                        highlightId={highlightId}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {hasNext && (
                <div className='padding-top-1rem'>
                    <div className='position-relative'>
                        <div
                            className='position-absolute top-0dot75rem border-bottom-style-solid border-bottom-width-0dot125rem border-bottom-color-gray-500 pointer-events-none'
                            style={{left: '-0.8rem', width: '0.6rem'}}
                        />
                        <div className='display-flex justify-content-flex-start'>
                            <Button {...{
                                size: 'md',
                                color: 'primary',
                                clickHandler: () => loadNext(8),
                                isLoading: isLoadingNext,
                                disabled: isLoadingNext
                            }}>
                                <FormattedMessage defaultMessage='More quotes' />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default QuotesList;


